import { ACTION, TradeSettleStatusEnum } from '@talos/kyoko';
import { uniq } from 'lodash-es';
import { useMemo } from 'react';
import { useRoleAuth } from '../../../../hooks/useRoleAuth';
import type { LPSettlementReportBlotterRow } from './blotter/lpSettlementReportRow';

interface CanSettleResult {
  canSettle: boolean;
  hasEditLPSettlemenyRole: boolean;
  reason?: string;
}

/**
 * Hook to determine if selected trades can be settled.
 * You can settle the list of selected trades if:
 * - You have the LP Settlement role
 * - There's at least one selected trade that's unsettled
 * - All selected trades are within the same Market Account
 */
export function useCanSettleSelectedTrades(trades: LPSettlementReportBlotterRow[]): CanSettleResult {
  const { isAuthorized } = useRoleAuth();
  const hasEditLPSettlemenyRole = isAuthorized(ACTION.EDIT_LP_SETTLEMENT);

  return useMemo(() => {
    if (!hasEditLPSettlemenyRole) {
      return {
        canSettle: false,
        hasEditLPSettlemenyRole: false,
        reason: 'You do not have permission to settle. Please contact your administrator.',
      };
    }

    const atLeastOneUnsettledTrade = trades.some(trade => trade.SettleStatus === TradeSettleStatusEnum.Unsettled);

    if (!atLeastOneUnsettledTrade) {
      return {
        canSettle: false,
        hasEditLPSettlemenyRole: true,
        reason: 'No unsettled trades are selected',
      };
    }

    const allTradesSameMarketAccount = uniq(trades.map(trade => trade.MarketAccount)).length === 1;
    if (!allTradesSameMarketAccount) {
      return {
        canSettle: false,
        hasEditLPSettlemenyRole: true,
        reason: 'Only trades from the same Market Account can be bulk settled.',
      };
    }

    return {
      canSettle: true,
      hasEditLPSettlemenyRole: true,
    };
  }, [trades, hasEditLPSettlemenyRole]);
}
